import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { CheckCircle2, Circle, ArrowRight, X, Rocket } from "lucide-react";

const STEPS = {
  bailleur: [
    { key: "bien", label: "Ajoutez votre premier bien", desc: "Créez la fiche de votre logement.", path: "/app/biens" },
    { key: "locataire", label: "Enregistrez un locataire", desc: "Ajoutez ses coordonnées et son bail.", path: "/app/locataires" },
    { key: "quittance", label: "Générez une quittance", desc: "Envoyez votre première quittance de loyer.", path: "/app/quittances" },
  ],
  agent: [
    { key: "bien", label: "Ajoutez un bien en portefeuille", desc: "Constituez votre catalogue.", path: "/app/biens" },
    { key: "locataire", label: "Ajoutez un locataire / prospect", desc: "Suivez vos contacts.", path: "/app/locataires" },
    { key: "location", label: "Créez une location", desc: "Associez un bien et un locataire.", path: "/app/locations" },
  ],
  gestionnaire: [
    { key: "bien", label: "Ajoutez un bien géré", desc: "Enregistrez un bien de votre client.", path: "/app/biens" },
    { key: "locataire", label: "Enregistrez un locataire", desc: "Ajoutez le locataire associé.", path: "/app/locataires" },
    { key: "finances", label: "Explorez le suivi financier", desc: "Visualisez revenus et dépenses.", path: "/app/finances" },
  ],
  courtier: [
    { key: "profil", label: "Complétez votre profil", desc: "Renseignez vos informations pro.", path: "/app/profil" },
    { key: "simulateur", label: "Testez le simulateur", desc: "Simulez un plan de financement.", path: "/app/simulateur" },
    { key: "offres", label: "Découvrez les offres & deals", desc: "Explorez les opportunités.", path: "/app/offres-deals" },
  ],
};

export default function GettingStarted() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const uid = user?.user_id || "anon";
  const steps = STEPS[user?.role];

  const [done, setDone] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`gs_done_${uid}`) || "[]"); } catch { return []; }
  });
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(`gs_dismissed_${uid}`) === "1");

  if (!steps || dismissed || user?.role === "bailleur") return null;

  const persist = (arr) => { try { localStorage.setItem(`gs_done_${uid}`, JSON.stringify(arr)); } catch {} };
  const toggle = (key, e) => {
    e.stopPropagation();
    const next = done.includes(key) ? done.filter((k) => k !== key) : [...done, key];
    setDone(next); persist(next);
    if (next.length >= steps.length) { localStorage.setItem(`gs_dismissed_${uid}`, "1"); setTimeout(() => setDismissed(true), 700); }
  };
  const dismiss = () => { localStorage.setItem(`gs_dismissed_${uid}`, "1"); setDismissed(true); };

  const count = done.length;
  const pct = Math.round((count / steps.length) * 100);

  return (
    <div className="rounded-2xl border border-brand-lime/40 bg-gradient-to-br from-brand-lime/10 to-white p-6" data-testid="getting-started">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="w-11 h-11 rounded-xl bg-brand-greenDeep text-brand-lime flex items-center justify-center shrink-0"><Rocket className="w-5 h-5" /></span>
          <div>
            <h3 className="font-display text-xl font-semibold text-brand-greenDeep">Bien démarrer sur 360immoinvest</h3>
            <p className="text-sm text-brand-green/60">3 étapes pour tirer le meilleur de votre espace.</p>
          </div>
        </div>
        <button onClick={dismiss} className="p-1.5 rounded-full text-brand-green/40 hover:text-brand-greenDeep hover:bg-brand-softer" title="Masquer" data-testid="gs-dismiss"><X className="w-4 h-4" /></button>
      </div>

      <div className="mt-4 mb-4">
        <div className="h-2 rounded-full bg-brand-soft overflow-hidden">
          <div className="h-full bg-brand-lime transition-all" style={{ width: `${pct}%` }} data-testid="gs-progress" />
        </div>
        <p className="text-xs text-brand-green/50 mt-1">{count}/{steps.length} terminé{count > 1 ? "s" : ""}</p>
      </div>

      <div className="space-y-2">
        {steps.map((s, i) => {
          const isDone = done.includes(s.key);
          return (
            <div key={s.key} onClick={() => navigate(s.path)} className="group flex items-center gap-3 rounded-xl border border-brand-soft bg-white p-3 cursor-pointer hover:border-brand-lime transition-colors" data-testid={`gs-step-${s.key}`}>
              <button onClick={(e) => toggle(s.key, e)} className={isDone ? "text-emerald-600" : "text-brand-green/30 hover:text-brand-green/60"} data-testid={`gs-check-${s.key}`}>
                {isDone ? <CheckCircle2 className="w-6 h-6" /> : <Circle className="w-6 h-6" />}
              </button>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-semibold ${isDone ? "text-brand-green/40 line-through" : "text-brand-greenDeep"}`}>{i + 1}. {s.label}</p>
                <p className="text-xs text-brand-green/50">{s.desc}</p>
              </div>
              <ArrowRight className="w-4 h-4 text-brand-green/30 group-hover:text-brand-limeDark group-hover:translate-x-0.5 transition-all" />
            </div>
          );
        })}
      </div>
    </div>
  );
}
